import React, { useEffect, useState } from 'react';
import { useReferrals } from '../hooks/useReferrals';
import { theme } from '../theme';
import { useResponsive } from '../hooks/useResponsive';

export const ReferralCard: React.FC = () => {
  const { isMobile } = useResponsive();
  const { stats, loading, referralCode, fetchStats, getOrCreateCode, copyCode, shareCode, shareViaWhatsApp } = useReferrals();
  const [feedback, setFeedback] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Auto-generar código si el usuario aún no tiene uno
    if (stats && !stats.referralCode) {
      getOrCreateCode();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats]);

  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 2500);
    return () => clearTimeout(t);
  }, [feedback]);

  const handleCopy = async () => {
    const ok = await copyCode();
    setFeedback(ok ? '✅ Código copiado' : '❌ No se pudo copiar');
  };

  const handleShare = async () => {
    if (isSharing) return;
    setIsSharing(true);
    try {
      const result = await shareCode();
      if (result === 'shared') setFeedback('✅ Compartido');
      else if (result === 'copied') setFeedback('✅ Mensaje copiado');
    } finally {
      setIsSharing(false);
    }
  };

  const handleWhatsApp = async () => {
    await shareViaWhatsApp();
  };

  if (loading && !stats) {
    return (
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: theme.spacing.lg,
        textAlign: 'center',
        color: theme.colors.textSecondary,
        fontFamily: theme.fonts.body,
      }}>
        Cargando referidos...
      </div>
    );
  }

  if (!stats) return null;

  const totalReferrals = stats.totalReferrals;
  const milestones = stats.milestones || [];
  const maxMilestone = milestones.length > 0 ? milestones[milestones.length - 1].count : 10;
  const progress = Math.min(totalReferrals / maxMilestone, 1);

  return (
    <div style={{
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.card,
      padding: isMobile ? theme.spacing.md : theme.spacing.lg,
      border: `1px solid ${theme.colors.border}`,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.md,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h3 style={{
          margin: 0,
          color: theme.colors.text,
          fontFamily: theme.fonts.heading,
          fontSize: isMobile ? '18px' : '20px',
          fontWeight: 700,
        }}>
          🎁 Invitá amigos
        </h3>
        <span style={{ color: theme.colors.textSecondary, fontSize: theme.fonts.sizeSmall }}>
          {totalReferrals} {totalReferrals === 1 ? 'referido' : 'referidos'}
        </span>
      </div>

      {/* Subtitle */}
      <p style={{
        margin: 0,
        color: theme.colors.textSecondary,
        fontSize: theme.fonts.sizeSmall,
        lineHeight: 1.4,
      }}>
        Compartí tu código. Cuando se registren y jueguen su primer partido, desbloqueás tags exclusivas para tu perfil.
      </p>

      {/* Code box (click to copy) */}
      <button
        type="button"
        onClick={handleCopy}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.spacing.sm,
          backgroundColor: theme.colors.background,
          border: `2px dashed ${theme.colors.border}`,
          borderRadius: theme.borderRadius.button,
          padding: '14px 20px',
          cursor: 'pointer',
          color: theme.colors.text,
          fontFamily: theme.fonts.heading,
          width: '100%',
          transition: 'border-color 0.15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = theme.colors.primary; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = theme.colors.border; }}
      >
        <span style={{ fontSize: '13px', color: theme.colors.textSecondary, letterSpacing: 0 }}>
          Tu código:
        </span>
        <span style={{
          fontSize: '22px',
          fontWeight: 800,
          letterSpacing: '2px',
          color: theme.colors.text,
        }}>
          {referralCode || '...'}
        </span>
        <span style={{ fontSize: '16px' }}>📋</span>
      </button>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleWhatsApp}
          style={{
            flex: '1 1 140px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 16px',
            backgroundColor: '#25D366',
            color: '#000',
            border: 'none',
            borderRadius: theme.borderRadius.button,
            fontWeight: 700,
            cursor: 'pointer',
            fontSize: theme.fonts.sizeBody,
            fontFamily: theme.fonts.body,
          }}
        >
          <span style={{ fontSize: '18px' }}>💬</span>
          WhatsApp
        </button>
        <button
          type="button"
          onClick={handleShare}
          disabled={isSharing}
          style={{
            flex: '1 1 140px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 16px',
            backgroundColor: theme.colors.primary,
            color: '#000',
            border: 'none',
            borderRadius: theme.borderRadius.button,
            fontWeight: 700,
            cursor: isSharing ? 'wait' : 'pointer',
            fontSize: theme.fonts.sizeBody,
            fontFamily: theme.fonts.body,
            opacity: isSharing ? 0.7 : 1,
          }}
        >
          <span style={{ fontSize: '18px' }}>🔗</span>
          {isSharing ? 'Compartiendo...' : 'Compartir'}
        </button>
      </div>

      {feedback && (
        <div style={{
          textAlign: 'center',
          color: theme.colors.primary,
          fontSize: theme.fonts.sizeSmall,
          fontFamily: theme.fonts.body,
        }}>
          {feedback}
        </div>
      )}

      {/* Progress bar */}
      <div>
        <div style={{
          height: '8px',
          backgroundColor: theme.colors.background,
          borderRadius: '4px',
          overflow: 'hidden',
          border: `1px solid ${theme.colors.border}`,
        }}>
          <div style={{
            height: '100%',
            width: `${progress * 100}%`,
            backgroundColor: theme.colors.primary,
            transition: 'width 0.3s ease',
          }} />
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '6px',
        }}>
          {milestones.map((m) => {
            const isUnlocked = totalReferrals >= m.count;
            return (
              <div key={m.count} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: isUnlocked ? theme.colors.primary : 'transparent',
                  border: `1px solid ${isUnlocked ? theme.colors.primary : theme.colors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: isUnlocked ? '#000' : theme.colors.textSecondary,
                  fontWeight: 700,
                }}>
                  {isUnlocked ? '✓' : '🔒'}
                </div>
                <span style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: isUnlocked ? theme.colors.primary : theme.colors.textSecondary,
                }}>
                  {m.count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Urgency text */}
      {stats.nextMilestone && stats.remainingForNext > 0 && (
        <div style={{
          textAlign: 'center',
          color: theme.colors.warning,
          fontWeight: 600,
          fontSize: theme.fonts.sizeSmall,
        }}>
          Te {stats.remainingForNext === 1 ? 'falta' : 'faltan'} {stats.remainingForNext}{' '}
          para desbloquear {stats.nextMilestone.tags.length > 0
            ? stats.nextMilestone.tags.join(', ')
            : 'el próximo nivel'}
        </div>
      )}

      {/* Tags unlocked summary */}
      {stats.unlockedTags.length > 0 && (
        <div style={{
          padding: theme.spacing.sm,
          backgroundColor: theme.colors.background,
          borderRadius: theme.borderRadius.card,
          border: `1px solid ${theme.colors.border}`,
        }}>
          <div style={{ fontSize: '11px', color: theme.colors.textSecondary, marginBottom: '4px' }}>
            Tags exclusivas:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {stats.unlockedTags.map((tag) => (
              <span key={tag} style={{
                padding: '3px 8px',
                backgroundColor: theme.colors.primary + '20',
                color: theme.colors.primary,
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: 600,
              }}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralCard;
